// src/features/expenses/components/ExpenseManagement/index.jsx
// ✅ يستخدم ExpenseManagement.module.css الخاص به

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Link }            from 'react-router-dom';
import api                 from '../../../../api/axiosInstance';
import StatCard            from '../../../../shared/components/ui/StatCard';
import styles              from './ExpenseManagement.module.css'; // ✅ ملفه الخاص
import { formatNumber, formatDate } from '../../../../shared/utils/formatters';
import * as XLSX           from 'xlsx';
import PrintableExpenseReport from '../../../../shared/components/print/PrintableExpenseReport';
import { useAuth }         from '../../../auth/context/AuthContext';
import GeneralExpenseForm  from '../GeneralExpenseForm';

function ExpenseManagement() {
  const [categories,   setCategories]   = useState([]);
  const [reportData,   setReportData]   = useState(null);
  const [filters,      setFilters]      = useState({
    start_date:   '',
    end_date:     '',
    expense_type: 'all',
    booking_type: '',
    description:  '',
    category_id:  '',
  });
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [settings,    setSettings]    = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const printableRef      = useRef(null);
  const { hasPermission } = useAuth();

  const uniqueDescriptions = useMemo(() => {
    if (!reportData?.expenses) return [];
    return Array.from(new Set(reportData.expenses.map(e => e.description)));
  }, [reportData]);

  const generateReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/expense-report/', { params: filters });
      setReportData(res.data);
    } catch {
      setError('حدث خطأ أثناء جلب تقرير المصاريف.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const init = async () => {
      try {
        const [settingsRes, categoriesRes] = await Promise.all([
          api.get('/settings/'),
          api.get('/expense-categories/'),
        ]);
        setSettings(settingsRes.data);
        setCategories(categoriesRes.data);
        generateReport();
      } catch {
        setError('فشل في تحميل البيانات الأولية.');
        setLoading(false);
      }
    };
    init();
  }, [generateReport]);

  const handleDeleteExpense = async (expenseId) => {
    if (!hasPermission('delete_expense')) {
      alert('لا تملك صلاحية حذف المصاريف.');
      return;
    }
    if (!window.confirm('هل أنت متأكد من حذف هذا المصروف؟')) return;
    try {
      await api.delete(`/expenses/${expenseId}/`);
      generateReport();
    } catch {
      setError('فشل في حذف المصروف.');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'expense_type' && value !== 'booking') {
      setFilters(prev => ({ ...prev, [name]: value, booking_type: '' }));
    } else {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleExpenseAdded = () => {
    setShowAddForm(false);
    generateReport();
  };

  const handleExport = () => {
    if (!reportData?.expenses?.length) {
      alert('لا توجد بيانات لتصديرها.');
      return;
    }
    const data = reportData.expenses.map(e => ({
      'نوع المصروف':    e.expense_type,
      'حساب المصروف':   e.category_name,
      'الوصف / البيان': e.description,
      'الحجز المرتبط':  e.booking_details ? e.booking_details.client.name : 'لا يوجد',
      'تاريخ الصرف':    formatDate(e.expense_date),
      'المبلغ':          parseFloat(e.amount),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'تقرير المصاريف');
    XLSX.writeFile(wb, `Expense_Report_${formatDate(new Date())}.xlsx`);
  };

  const handlePrint = useReactToPrint({
    contentRef:    printableRef,
    documentTitle: `Expense_Report_${filters.start_date}_to_${filters.end_date}`,
  });

  return (
    <div className={styles.container}>

      {/* مخفي للطباعة */}
      <div style={{ display: 'none' }}>
        <PrintableExpenseReport
          ref={printableRef}
          reportData={reportData}
          filters={filters}
          settings={settings}
        />
      </div>

      {/* ── الهيدر ── */}
      <header className={styles.header}>
        <h1>إدارة وعرض المصاريف</h1>
      </header>

      {/* ── الفلاتر ── */}
      <div className={styles.filterBox}>
        <div className={styles.filterBoxHeader}>
          <h3>الفلاتر والأدوات</h3>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            {hasPermission('add_expense') && (
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className={styles.generateButton}
                style={{ background: showAddForm ? 'var(--text-muted)' : undefined }}
              >
                {showAddForm ? 'إغلاق النموذج' : '+ إضافة مصروف عام'}
              </button>
            )}
            <button
              onClick={handleExport}
              className={styles.exportButton}
              disabled={!reportData}
            >
              تصدير Excel
            </button>
            <button
              onClick={handlePrint}
              className={styles.exportButton}
              disabled={!reportData}
              style={{ borderColor: 'var(--color-info)', color: 'var(--color-info-text)' }}
            >
              طباعة PDF
            </button>
          </div>
        </div>

        {showAddForm && <GeneralExpenseForm onExpenseAdded={handleExpenseAdded} />}

        <div className={styles.filterControls}>
          <input
            type="text"
            name="description"
            placeholder="ابحث حسب وصف المصروف"
            value={filters.description}
            onChange={handleFilterChange}
            style={{ gridColumn: '1 / -1' }}
            list="expense-descriptions"
          />
          <datalist id="expense-descriptions">
            {uniqueDescriptions.map((d, i) => <option key={i} value={d} />)}
          </datalist>

          <input
            type="date" name="start_date"
            value={filters.start_date}
            onChange={handleFilterChange}
            title="من تاريخ"
          />
          <input
            type="date" name="end_date"
            value={filters.end_date}
            onChange={handleFilterChange}
            title="إلى تاريخ"
          />

          <select name="category_id" value={filters.category_id} onChange={handleFilterChange}>
            <option value="">كل حسابات المصاريف</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select name="expense_type" value={filters.expense_type} onChange={handleFilterChange}>
            <option value="all">كل أنواع المصاريف</option>
            <option value="general">المصاريف العامة</option>
            <option value="booking">مصاريف الحجوزات</option>
          </select>

          {filters.expense_type === 'booking' && (
            <select name="booking_type" value={filters.booking_type} onChange={handleFilterChange}>
              <option value="">كل أنواع الحجوزات</option>
              <option value="graduation">حفلات التخرج</option>
              <option value="wedding">الأعراس</option>
              <option value="kids">تصوير أطفال</option>
              <option value="event">مناسبات</option>
              <option value="special">طلب خاص</option>
              <option value="digital_marketing">تسويق إلكتروني</option>
            </select>
          )}

          <button
            onClick={generateReport}
            disabled={loading}
            className={styles.generateButton}
          >
            {loading ? 'جاري التحديث...' : 'تحديث التقرير'}
          </button>
        </div>
      </div>

      {error   && <p className={styles.errorMessage}>{error}</p>}
      {loading && (
        <p style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--text-muted)' }}>
          جاري تحميل البيانات...
        </p>
      )}

      {/* ── النتائج ── */}
      {reportData && (
        <div className={styles.resultsContainer}>

          {/* إحصائية واحدة */}
          <div className={styles.statsGrid}>
            <StatCard
              title="إجمالي المصاريف المطابقة للفلتر"
              value={reportData.total_expenses}
              unit="د.ع"
              color="expense"
              decimals={2}
            />
          </div>

          {/* الجدول */}
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>نوع المصروف</th>
                  <th>حساب المصروف</th>
                  <th>الوصف / البيان</th>
                  <th>الحجز المرتبط</th>
                  <th>تاريخ الصرف</th>
                  <th>المبلغ</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {reportData.expenses.length > 0 ? (
                  reportData.expenses.map(expense => (
                    <tr key={expense.id}>
                      <td data-label="نوع المصروف">{expense.expense_type}</td>
                      <td data-label="حساب المصروف"><strong>{expense.category_name}</strong></td>
                      <td data-label="الوصف">{expense.description}</td>
                      <td data-label="الحجز المرتبط">
                        {expense.booking_details ? (
                          <Link
                            to={`/booking/${expense.booking_details.id}`}
                            className={styles.detailsButton}
                          >
                            {expense.booking_details.client.name}
                          </Link>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      {/* ✅ تاريخ موحد DD/MM/YYYY */}
                      <td data-label="تاريخ الصرف">{formatDate(expense.expense_date)}</td>
                      <td data-label="المبلغ" className={styles.amountCell}>
                        {formatNumber(expense.amount, 2)}
                      </td>
                      <td data-label="الإجراءات" className={styles.actionsCell}>
                        {hasPermission('delete_expense') && (
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className={`${styles.detailsButton} ${styles.deleteButton}`}
                          >
                            حذف
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-muted)' }}>
                      لا توجد مصاريف تطابق الفلاتر المحددة.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>
      )}

    </div>
  );
}

export default ExpenseManagement;